import React, { useEffect, useState } from 'react';
import Header from '../Header/Header';
import { useNavigate } from 'react-router-dom';
import { CustomTable, ConfirmationPopup, SuccessModal, Pagination, Search, Loader } from '@ohif/ui';
import { _patientUrls } from '@custom/Utils/constants/apiUrls';
import { calculateAge, capitalizeWords, customStyle, formatDate, getCurrencyContact } from '@custom/Utils/utils';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import patientList from '@custom/rtk-apis/patients/patientList';
import { closePatientListModal } from '@custom/store/slices/Patients/patientlistSlice';
import deletePatient from '@custom/rtk-apis/patients/deletePatient';
import { closePatientDeleteModal } from '@custom/store/slices/Patients/deletePatientSlice';
import deactivatePatientApi from '@custom/rtk-apis/patients/deactivatePatient';
import activatePatientApi from '@custom/rtk-apis/patients/activatePatient';
import { closeDeactivatePatientListModal } from '@custom/store/slices/Patients/deactivatePatient';
import { useTheme } from "../../ui/src/contextProviders"
import { _roleType } from '@custom/Utils/constants/appConstants';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import CryptoJS from 'crypto-js';

const PatientList = props => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userRole = sessionStorage?.getItem("role")
  const [tableData, setTableData] = useState([])
  const [search, setSearch] = useState('');
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(1000000);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const [title, setTitle] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [buttonText, setButtonText] = useState("Deactivated Patients");
  const [actionType, setActionType] = useState('');
  const [showDeleteAction, setShowDeleteAction] = useState(true);
  const [pagination, setPagination] = useState({ totalCount: 0, perPage: 30, currentPage: 1, totalPages: 0 });
  const { msg, result, loading, status } = useSelector((state: RootState) => state?.patientListResponse);
  const deletePatientResponse = useSelector((state: RootState) => state?.deletePatientResponse);
  const { message: deactivateMsg, result: deactivateResult, loading: deactivateLoading, status: deactivateStatus } = useSelector((state: RootState) => state?.deactivatePatientSliceResponse);
  const { isDarkMode } = useTheme();
  const billingGeneralSettingsData = useSelector((state: RootState) => state?.getBillingGeneralSettingsSliceResponse);
  const [rawPatientData, setRawPatientData] = useState(null);

  const handleDeactivatePatient = () => {
    if (buttonText === "Deactivated Patients") {
      fetchDeactivatedPatientList();
      setButtonText("Activated Patients");
      setShowDeleteAction(false);
    } else {
      fetchPatientList();
      setButtonText("Deactivated Patients");
      setShowDeleteAction(true);
    }
  };

  const columns = [
    { key: 'name', title: 'Name', sort: true },
    { key: 'age', title: 'Age', sort: true },
    { key: 'gender', title: 'Gender' },
    { key: 'regDate', title: 'Reg Date', sort: true },
    ...(userRole === 'hospitalsuperadmin' || userRole === 'Admin'
      ? [{ key: 'scanSample', title: 'Department Name' }]
      : []),
    { key: 'orgType', title: 'Patient Type' },
    { key: 'uhid', title: 'UHID' },
    { key: 'contactNo', title: 'Contact No.' },
    { key: 'actions', title: 'Action' }
  ];

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        if (item?.patientDetail?.deletedAt !== null) {
          setSelectedRow(item);
          setActionType('activate');
          setIsPopupOpen(true);
        } else {
          navigate("/addPatient", { state: { patientDetails: item, mode: 'edit', userRole: item?.patientDetail?.createdByDetails?.role } });
        }
      }
    },
    ...(buttonText === "Deactivated Patients" ? [{
      label: 'Deactivate',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setActionType('deactivate');
        setIsPopupOpen(true);
      }
    }] : []),
  ];

  const navigateToPage = () => {
    navigate("/addpatient", { state: { mode: "create" } })
  }

  const handleActivateConfirm = (item) => {
    if (selectedRow?.patientDetail?._id) {
      dispatch(activatePatientApi({ userId: selectedRow?.patientDetail?._id }));
      setIsSuccessModal(true);
      setTitle('Patient activated successfully.');
      setTimeout(() => {
        setIsPopupOpen(false);
        fetchPatientList();
        setButtonText('Deactivated Patients');
      }, 300);
    } else {
      console.error('Patient ID is undefined');
    }
  };

  const handleConfirm = async () => {
    if (actionType === 'deactivate') {
      await dispatch(deletePatient({ _id: selectedRow?.patientDetail?._id }));
    } else if (actionType === 'activate') {
      await handleActivateConfirm(selectedRow?.patientDetail?._id);
    }
    setIsPopupOpen(false);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const capitaliseLetter = str => {
    if (!str) {
      return null;
    }
    return str
      .split(/[ _]/)
      .map(word => {
        if (word) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        } else {
          return '';
        }
      })
      .join(' ');
  };

  const handlePageSizeChange = e => {
    setPgLimit(e);
  };

  const onPageChangeHandler = pg => {
    setPgNumber(pg);
  };

  useEffect(() => {
    if (buttonText === 'Deactivated Patients') {
      fetchPatientList()
    } else {
      fetchDeactivatedPatientList()
    }
  }, [search, pgLimit, pgNumber])

  useEffect(() => {
    if (deletePatientResponse?.status === "SUCCESS") {
      setIsSuccessModal(true)
      setTitle('User deleted successfully.');
      setTimeout(() => {
        setIsSuccessModal(false)
        fetchPatientList()
      }, 500)
    }
    dispatch(closePatientDeleteModal())
  }, [deletePatientResponse])

  const getHospitalId = () =>  sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId");

  const buildPatientListParams = () => ({
    pgLimit,
    pageNumber: pgLimit * (pgNumber - 1),
    hospitalId: getHospitalId(),
    searchQ: search,
    patientId: ""
  });

  useEffect(() => {
    if (!rawPatientData) return;

    const AES_SECRET_KEY = process.env.NEXT_PUBLIC_AES_SECRET_KEY;
    const AES_IV = process.env.NEXT_PUBLIC_AES_IV;

    const decryptData = (encryptedData) => {
      try {
        if (!AES_SECRET_KEY || !AES_IV) {
          return null;
        }

        const key = CryptoJS.enc.Hex.parse(AES_SECRET_KEY);
        const iv = CryptoJS.enc.Hex.parse(AES_IV);

        const cipherParams = CryptoJS.enc.Hex.parse(encryptedData);
        const decrypted = CryptoJS.AES.decrypt({ ciphertext: cipherParams }, key, {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });

        const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

        if (!decryptedText) {
          return null;
        }

        return decryptedText;
      } catch (error) {
        return null;
      }
    };

    const formatPatientData = (list) => {
      if (!Array.isArray(list) || list.length === 0) {
        return [];
      }

      const formatted = list
        .map((item) => {
          if (!item || !item.firstName || !item.lastName) {
            return null;
          }

          return {
            name: capitaliseLetter(`${item?.firstName} ${item?.lastName}`) || '--',
            age: calculateAge(item?.dob) || '--',
            gender: item?.gender || '--',
            regDate: formatDate(item?.createdAt) || '--',
            scanSample: item?.specialField || '--',
            uhid: item?.UHID || '--',
            patientDetail: item || '--',
            contactNo: `${item?.contactInfo?.dialCode} ${item?.contactInfo?.mobileNo}` || '--',
            orgType: capitalizeWords(item?.orgId?.type) || '--',
          };
        })
        .filter(item => item !== null);

      return formatted;
    };

    let patientData = [];
    let paginationObj = {};
    if (typeof rawPatientData === 'string') {
      const decryptedData = decryptData(rawPatientData);
      if (decryptedData) {
        try {
          const parsedData = JSON.parse(decryptedData);
          if (parsedData.list && Array.isArray(parsedData.list)) {
            patientData = parsedData.list;
            paginationObj = parsedData.pagination || {};
          } else {
            setTableData([]);
            setTotalPg(0);
            setPagination({ totalCount: 0, perPage: pgLimit, currentPage: pgNumber, totalPages: 0 });
            return;
          }
        } catch (parseError) {
          setTableData([]);
          setTotalPg(0);
          setPagination({ totalCount: 0, perPage: pgLimit, currentPage: pgNumber, totalPages: 0 });
          return;
        }
      } else {
        setTableData([]);
        setTotalPg(0);
        setPagination({ totalCount: 0, perPage: pgLimit, currentPage: pgNumber, totalPages: 0 });
        return;
      }
    } else if (rawPatientData?.list) {
      patientData = rawPatientData.list;
      paginationObj = rawPatientData.pagination || {};
    }

    if (paginationObj.totalPages) setTotalPg(paginationObj.totalPages);
    if (paginationObj.currentPage) setPgNumber(paginationObj.currentPage);

    setPagination({
      totalCount: paginationObj.totalCount || 0,
      perPage: paginationObj.perPage || pgLimit,
      currentPage: paginationObj.currentPage || pgNumber,
      totalPages: paginationObj.totalPages || 0,
    });

    const formattedData = formatPatientData(patientData);
    setTableData(formattedData);
  }, [rawPatientData]);

  const fetchPatientList = async () => {
    const response = await dispatch(patientList(buildPatientListParams()));
    const totalItems = response.payload.result.count || 0;
    if (response?.payload?.result) {
      setRawPatientData(response.payload.result);
      setTotalPg(Math.ceil(totalItems / pgLimit));
    } else if (response?.payload?.result?.list) {
      setRawPatientData({ list: response.payload.result.list });
      setTotalPg(Math.ceil(totalItems / pgLimit));
    } else {
      setTableData([]);
      setTotalPg(0);
    }
  };

  const fetchDeactivatedPatientList = async () => {
    const response = await dispatch(deactivatePatientApi(buildPatientListParams()));
    const totalItems = response.payload.result.count || 0;
    if (response?.payload?.result) {
      setRawPatientData(response.payload.result);
      setTotalPg(Math.ceil(totalItems / pgLimit));
    } else if (response?.payload?.result?.list) {
      setRawPatientData({ list: response.payload.result.list });
      setTotalPg(Math.ceil(totalItems / pgLimit));
    } else {
      setTableData([]);
      setTotalPg(0);
    }
  };

  useEffect(() => {
    dispatch(getBillingGeneralSettings());
  }, [])

  useEffect(() => {
    if (deletePatientResponse?.status === "SUCCESS") {
      setIsSuccessModal(true);
      setTitle(deletePatientResponse?.msg);
      setTimeout(() => {
        setIsSuccessModal(false);
        fetchPatientList();
      }, 500)
    }
    dispatch(closePatientDeleteModal());
  }, [deletePatientResponse]);

  const getActionButton = (actions) => {
    if (userRole === 'Radiologist' || userRole === 'Clinician' || userRole === 'Nurse' || userRole === 'Resident' || userRole === _roleType.pathologist || userRole === _roleType.government) {
      return []
    }
    else {
      return actions
    }
  }

  const removeActionColumn = (columns) => {
    if (userRole === 'Radiologist' || userRole === 'Clinician' || userRole === 'Nurse' || userRole == 'Resident' || userRole === _roleType.pathologist || userRole === _roleType.government) {
      const filteredColumns = columns.filter(column => column.key !== 'actions');
      return filteredColumns
    }
    else {
      return columns
    }
  }

  const searchClick = searchText => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);
  };

  return (
    <>
      <div className='page page__fullBody' style={
        isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header title='Patients'></Header>
        <div className='page__body'>
          <div className='tableWrap'>
            <div className='tableWrap__head'>
              <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search text="Name, Contact Details or UHID" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue} />
                </div>
              </div>
              {userRole !== 'Radiologist' && userRole !== 'Clinician' && userRole !== 'Nurse' && userRole !== 'Resident' && userRole !== _roleType.pathologist && userRole !== _roleType.government && <div className='btn__group'>
                <>
                  <button className="btn btn__white" onClick={handleDeactivatePatient}>
                    {buttonText}
                  </button>
                  <button className="btn btn__white" onClick={navigateToPage}>
                    <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Add New Patient
                  </button>
                </>
              </div>
              }
            </div>
            <div className='tableContainer'>
              <CustomTable columns={removeActionColumn(columns)} data={tableData} actions={getActionButton(actions)} searchQuery={searchQuery} from="patient" userDetails={''} setClickedItem={''} clickedItem={''} />
            </div>
          </div>
        </div>
        {tableData.length > 0 && <Pagination
          onChangePage={onPageChangeHandler}
          onChangePerPage={handlePageSizeChange}
          totalPage={pagination.totalCount}
          currentPage={pagination.currentPage}
          perPage={pagination.perPage}
        />}

      </div>
      <Loader isLoaderOpen={loading || deactivateLoading} />
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to ${actionType} ${selectedRow?.name}?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={''}
      />
      <SuccessModal
        isSuccessModalOpen={isSuccessModalOpen}
        title={title}
        handleCloseModal={() => setIsSuccessModal(false)}

      />
    </>
  )
};
export default PatientList;
